// VetServiceCheckinCard.jsx – with vitals
// (boarding-style tabs) + readOnly mode, staff preselection fix, and price breakdown
import { useState, useEffect, useRef } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import {
  FaUndo,
  FaEllipsisV,
  FaInfoCircle,
  FaCheckCircle,
} from "react-icons/fa";
import BookingAddonsSection from "./BookingAddonsSection";

// ---------- Helper: ensure time is in 12-hour format ("09:00 am") ----------
const format12Hour = (timeStr) => {
  if (!timeStr) return "";
  if (/(am|pm)/i.test(timeStr)) return timeStr.trim();
  const parts = timeStr.split(":");
  const h = parseInt(parts[0], 10);
  const m = parts[1] || "00";
  const period = h >= 12 ? "pm" : "am";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const VITAL_FIELDS = [
  { key: "weight", label: "Weight", unit: "kg", placeholder: "0.0" },
  { key: "temp", label: "Temp", unit: "°F", placeholder: "0.0" },
  { key: "hr", label: "HR", unit: "bpm", placeholder: "0" },
  { key: "rr", label: "RR", unit: "/min", placeholder: "0" },
  { key: "bcs", label: "BCS", unit: "", placeholder: "0" },
  { key: "painScore", label: "Pain Score", unit: "", placeholder: "0" },
];

const VetServiceCheckinCard = ({
  pet,
  service,
  index,
  onUpdate,
  clientId,
  onCancel,
  onModify,
  readOnly = false,
  // onSave,
}) => {
  // ---------- Local state (UI values) ----------
  const [serviceId, setServiceId] = useState(service.service_id || "");
  const [staffId, setStaffId] = useState(service.technician_id || "");
  const [selectedTime, setSelectedTime] = useState(
    format12Hour(service.startTime || ""),
  );
  const [selectedDate, setSelectedDate] = useState(service.serviceDate || "");

  // ---------- Vitals state ----------
  const [vitals, setVitals] = useState(
    service.vitals || {
      weight: "",
      temp: "",
      hr: "",
      rr: "",
      bcs: "",
      painScore: "",
    },
  );

  const handleVitalChange = (key, value) => {
    const newVitals = { ...vitals, [key]: value };
    setVitals(newVitals);
    onUpdate({ ...service, vitals: newVitals });
  };

  // ---------- Original snapshot ----------
  const originalSnapshot = useRef(null);
  useEffect(() => {
    if (!originalSnapshot.current) {
      originalSnapshot.current = JSON.parse(JSON.stringify(service));
    }
  }, []);

  // Track initial mount to prevent price calculation on load
  const isInitialMount = useRef(true);

  // ---------- Data from API ----------
  const [vetServices, setVetServices] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);

  const selectedVetService = vetServices.find((s) => s._id === serviceId);
  const selectedStaff = availableStaff.find((s) => s._id === staffId);
  const lastPriceParamsRef = useRef("");

  // ---------- Detect changes vs original snapshot ----------
  const hasChanges =
    originalSnapshot.current &&
    (originalSnapshot.current.service_id !== service.service_id ||
      originalSnapshot.current.price !== service.price);

  // ---------- Reset function ----------
  const handleReset = () => {
    if (!originalSnapshot.current) return;
    const snap = JSON.parse(JSON.stringify(originalSnapshot.current));
    setServiceId(snap.service_id || "");
    setStaffId(snap.technician_id || "");
    setSelectedTime(format12Hour(snap.startTime || ""));
    setSelectedDate(snap.serviceDate || "");
    setVitals(
      snap.vitals || {
        weight: "",
        temp: "",
        hr: "",
        rr: "",
        bcs: "",
        painScore: "",
      },
    );
    onUpdate({
      ...snap,
      serviceName: snap.serviceName || "",
    });
  };

  // ---------- Click outside for breakdown ----------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        breakdownRef.current &&
        !breakdownRef.current.contains(event.target)
      ) {
        setShowBreakdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- 1. Fetch vet services ----------
  useEffect(() => {
    if (readOnly) return;
    const fetchVetServices = async () => {
      try {
        const res = await httpService.get(
          `/vendorvetclinicservice/getvendorvetclinicservices?status=active`,
        );
        const availableServices = (res?.data || []).filter(
          (service) =>
            service.status === "active" && service.is_configured === true,
        );
        setVetServices(availableServices);

        if (serviceId && !service.serviceName) {
          const foundService = availableServices.find(
            (s) => s._id === serviceId,
          );
          if (foundService) {
            const updatedService = {
              ...service,
              serviceName: foundService.name,
              serviceId: serviceId,
            };
            onUpdate(updatedService);
          }
        }
      } catch (err) {
        console.error("Error fetching vet services:", err);
        toast.error("Failed to load vet services");
      }
    };
    fetchVetServices();
  }, [readOnly]);

  // ---------- 2. Fetch service details ----------
  useEffect(() => {
    if (readOnly) return;
    const fetchServiceDetails = async () => {
      if (!serviceId) {
        setSelectedServiceDetails(null);
        return;
      }
      try {
        const res = await httpService.get(
          `/vendorvetclinicservice/getvendorvetclinicservice/${serviceId}`,
        );
        setSelectedServiceDetails(res.data);
        const updatedService = {
          ...service,
          serviceId,
          serviceName: res.data?.name || "",
          duration: formatDuration(res.data?.duration, pet?.size || ""),
          service_type_name:
            res.data?.service_category?.service_type_name || "vet",
        };
        onUpdate(updatedService);
      } catch (err) {
        console.error("Error fetching service details:", err);
        toast.error("Failed to load service details");
      }
    };
    fetchServiceDetails();
  }, [serviceId, readOnly]);

  // ---------- 3. Fetch available staff (with preselection fix) ----------
  useEffect(() => {
    if (readOnly) {
      setAvailableStaff([]);
      return;
    }
    const fetchAvailableStaff = async () => {
      if (!serviceId || !selectedDate || !selectedTime) {
        setAvailableStaff([]);
        return;
      }
      setLoadingStaff(true);
      try {
        const duration = selectedServiceDetails?.duration;
        const durationMinutes = getDurationInMinutes(duration, pet?.size || "");
        const timeRange = getTimeRange(selectedTime, durationMinutes);
        if (!timeRange) {
          setAvailableStaff([]);
          return;
        }
        const payload = {
          date: selectedDate,
          timeSlots: timeRange,
          serviceCategoryTypeId: selectedServiceDetails?.service_category?._id,
          appointmentId: service?.ref_id || null,
        };
        const res = await httpService.post(
          "/employees/getAvailableVendorEmployees",
          payload,
        );
        let filteredStaff = res.data.filter(
          (staff) => staff.is_available !== false,
        );

        // ✅ Staff preselection fix
        if (staffId && !filteredStaff.some((s) => s._id === staffId)) {
          filteredStaff = [
            ...filteredStaff,
            {
              _id: staffId,
              name: service.assignedStaff || "Assigned Staff",
              is_available: true,
            },
          ];
        }

        setAvailableStaff(filteredStaff);
      } catch (err) {
        console.error("Error fetching available staff:", err);
        toast.error("Failed to load staff");
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchAvailableStaff();
  }, [selectedDate, selectedTime, serviceId, selectedServiceDetails, readOnly]);

  // ---------- 4. Fetch business hours ----------
  useEffect(() => {
    if (readOnly) return;
    if (selectedDate && serviceId) {
      httpService
        .get("/vethours/getvetbusinesshours")
        .then((res) => setBusinessHours(res?.data || res))
        .catch(() => toast.error("Failed to load business hours"));
    }
  }, [selectedDate, serviceId, readOnly]);

  const hasUserInteracted = useRef(false);
  // ---------- 5. Price calculation (only after user changes) ----------
  useEffect(() => {
    if (readOnly) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!hasUserInteracted.current) return;

    if (!hasChanges) return;

    const calculatePrice = async () => {
      if (!clientId || !pet?.id || !serviceId || !selectedDate) return;

      const paramsKey = `${clientId}_${pet.id}_${serviceId}_${selectedDate}`;
      if (lastPriceParamsRef.current === paramsKey) return;
      lastPriceParamsRef.current = paramsKey;
      setPriceLoading(true);

      try {
        const payload = {
          client_id: clientId,
          pet_id: pet.id,
          vendor_service_id: serviceId,
        };
        const response = await httpService.post(
          "/vendorbooking/getPriceCalculation?service_type=vet_clinic",
          payload,
        );

        let salePrice = 0,
          taxAmount = 0,
          taxPercentage = 0,
          discountAmount = 0,
          surchargeDetails = [];
        if (typeof response.data === "number") {
          salePrice = response.data;
        } else if (response.data) {
          salePrice = response.data.sale_price || response.data.price || 0;
          taxAmount =
            response.data.discounted_tax || response.data.taxAmount || 0;
          taxPercentage = response.data.tax_percentage || 0;
          discountAmount = response.data.discount_amount || 0;
          surchargeDetails = response.data.surchargeDetails || [];
        }

        const isFree = response.data?.discount_percentage === 100;
        const finalPrice = isFree ? 0 : salePrice;
        const effectiveTax = isFree ? 0 : taxAmount;
        const surchargesTotal = surchargeDetails.reduce(
          (sum, s) => sum + (Number(s.price) || 0),
          0,
        );

        const updatedService = {
          ...service,
          serviceId,
          serviceName: selectedVetService?.name || "",
          assignedStaff: selectedStaff?.name || "",
          assigned_staff_id: staffId,
          serviceDate: selectedDate,
          startTime: selectedTime,
          price: finalPrice + surchargesTotal + effectiveTax,
          unit_price: salePrice,
          sale_price: salePrice,
          tax_percentage: taxPercentage,
          gst_amount: effectiveTax,
          discount_amount: discountAmount,
          price_surcharge: surchargeDetails,
          surchargeDetails,
          surchargesTotal,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "vet",
        };
        onUpdate(updatedService);
      } catch (error) {
        console.error("Error calculating price:", error);
        const updatedService = {
          ...service,
          serviceId,
          serviceName: selectedVetService?.name || "",
          assignedStaff: selectedStaff?.name || "",
          assigned_staff_id: staffId,
          serviceDate: selectedDate,
          startTime: selectedTime,
          price: selectedVetService?.basic_price || 0,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "vet",
          price_surcharge: [],
          surchargeDetails: [],
          surchargesTotal: 0,
          sale_price: 0,
        };
        onUpdate(updatedService);
      } finally {
        setPriceLoading(false);
      }
    };
    calculatePrice();
  }, [
    clientId,
    pet?.id,
    serviceId,
    selectedDate,
    selectedTime,
    staffId,
    selectedVetService,
    selectedStaff,
    readOnly,
  ]);

  // ---------- Helpers (unchanged) ----------
  const getDurationInMinutes = (durationData, petSize) => {
    if (!durationData) return 0;
    let targetDuration = null;
    if (durationData.duration_based_on === "flat") {
      targetDuration = durationData.flat;
    } else {
      const sizeKey = petSize?.toLowerCase() || "flat";
      const validSizes = ["small", "medium", "large", "giant"];
      const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
      targetDuration = durationData[selectedSize] || durationData.flat;
    }
    if (!targetDuration) return 0;
    const { hour = 0, minutes = 0 } = targetDuration;
    return hour * 60 + minutes;
  };

  const formatDuration = (durationData, petSize) => {
    if (!durationData) return "";
    let targetDuration = null;
    if (durationData.duration_based_on === "flat") {
      targetDuration = durationData.flat;
    } else {
      const sizeKey = petSize?.toLowerCase() || "flat";
      const validSizes = ["small", "medium", "large", "giant"];
      const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
      targetDuration = durationData[selectedSize] || durationData.flat;
    }
    if (!targetDuration) return "";
    const { hour = 0, minutes = 0 } = targetDuration;
    const parts = [];
    if (hour > 0) parts.push(`${hour} Hour${hour > 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} Minute${minutes > 1 ? "s" : ""}`);
    return parts.join(" ");
  };

  const getTimeRange = (startTime12h, durationMinutes) => {
    if (!startTime12h || !durationMinutes) return "";
    const match = startTime12h.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
    if (!match) return "";
    let [_, hour, minute, period] = match;
    hour = parseInt(hour, 10);
    minute = parseInt(minute, 10);
    period = period.toLowerCase();
    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;
    const startDate = new Date();
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const format = (d) => {
      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      return `${h}:${m}`;
    };
    return `${format(startDate)}-${format(endDate)}`;
  };

  const generateTimeSlots = (businessHours, appointmentDate, interval = 15) => {
    if (!businessHours?.timeSlots?.length) return [];
    const slots = [];
    const today = new Date();
    const isToday =
      appointmentDate &&
      new Date(appointmentDate).toDateString() === today.toDateString();
    const roundedNow =
      Math.ceil((today.getHours() * 60 + today.getMinutes()) / interval) *
      interval;
    businessHours.timeSlots.forEach((range) => {
      let current = new Date(`2000-01-01 ${range.start}`);
      const end = new Date(`2000-01-01 ${range.end}`);
      while (current < end) {
        const slotMinutes = current.getHours() * 60 + current.getMinutes();
        slots.push(
          current
            .toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
            .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase()),
        );
        current = new Date(current.getTime() + interval * 60000);
      }
    });
    return slots;
  };

  const timeSlots = generateTimeSlots(businessHours, selectedDate);

  const formattedDate = service.serviceDate
    ? new Date(service.serviceDate).toLocaleDateString("en-IN", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
    : "Not set";

  // ---------- Boarding data handlers ----------
  const addFeeding = (item) => {
    const updated = [...(service.feeding || []), item];
    onUpdate({ ...service, feeding: updated });
  };
  const removeFeeding = (idx) => {
    const updated = (service.feeding || []).filter((_, i) => i !== idx);
    onUpdate({ ...service, feeding: updated });
  };

  const addWalking = (item) => {
    const updated = [...(service.walking || []), item];
    onUpdate({ ...service, walking: updated });
  };
  const removeWalking = (idx) => {
    const updated = (service.walking || []).filter((_, i) => i !== idx);
    onUpdate({ ...service, walking: updated });
  };

  const addMedication = (item) => {
    const updated = [...(service.medication || []), item];
    onUpdate({ ...service, medication: updated });
  };
  const removeMedication = (idx) => {
    const updated = (service.medication || []).filter((_, i) => i !== idx);
    onUpdate({ ...service, medication: updated });
  };

  const addBelonging = (item) => {
    const updated = [...(service.belongings || []), item];
    onUpdate({ ...service, belongings: updated });
  };
  const removeBelonging = (idx) => {
    const updated = (service.belongings || []).filter((_, i) => i !== idx);
    onUpdate({ ...service, belongings: updated });
  };

  // Custom select
  const CustomSelect = ({
    value,
    onChange,
    disabled,
    loading,
    options,
    placeholder,
    className = "",
    labelKey = "name",
    valueKey = "_id",
    renderOption = null,
  }) => {
    const valueExists = options.some(
      (opt) => String(opt[valueKey]) === String(value),
    );
    const displayValue = valueExists ? value : "";
    return (
      <div className={`relative flex-shrink-0 ${className}`}>
        <select
          value={displayValue}
          onChange={onChange}
          disabled={disabled}
          className={`appearance-none bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block w-full pl-3 pr-10 py-2.5 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${
            disabled ? "text-gray-400" : ""
          }`}
        >
          <option value="">{loading ? "Loading..." : placeholder}</option>
          {options.map((opt) => (
            <option key={opt[valueKey]} value={opt[valueKey]}>
              {renderOption
                ? renderOption(opt)
                : opt[labelKey] || opt.full_name || opt.name}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <svg
            className={`w-4 h-4 ${disabled ? "text-gray-300" : "text-gray-500"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  };

  // Surcharge list fallback
  const surchargeList = service.surchargeDetails || service.price_surcharge || [];
  const surchargeTotal =
    service.surchargesTotal ??
    surchargeList.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <div className="px-4 py-3">
        {/* ===== HEADER: Badge + date + status + menu (left) | Info + Price + reset (right) ===== */}
        <div className="flex items-center justify-between mb-3">
          {/* Left section */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold uppercase px-2.5 py-1 rounded-full bg-green-100 text-green-800">
              Vet
            </span>
            <span className="text-sm font-medium text-gray-700">
              {formattedDate}
            </span>
            <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md">
              {service.status === "confirmed" && service.is_queue == 0
                ? "Scheduled"
                : service.status === "" && service.is_queue == 1
                  ? "Queue"
                  : service.status}
            </span>
            {/* Three-dot menu */}
            {service.status != "cancelled" && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                className="p-1 rounded-full hover:bg-gray-200 transition"
                title="More actions"
              >
                <FaEllipsisV size={14} className="text-gray-600" />
              </button>
              {menuOpen && (
                <div className="absolute left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {/* {service.status != "cancelled" && ( */}
                    <button
                      className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-t-lg"
                      onClick={() => {
                        setMenuOpen(false);
                        onCancel && onCancel(service);
                      }}
                    >
                      Cancel
                    </button>
                  {/* )} */}
                  <button
                    className="block w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-b-lg"
                    onClick={() => {
                      setMenuOpen(false);
                      onModify && onModify(service);
                    }}
                  >
                    Modify
                  </button>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Right section: Info icon + Price + Reset + diff */}
          <div className="flex items-center gap-2 flex-col">
            <div
              className="flex items-center gap-2 relative"
              ref={breakdownRef}
            >
              {priceLoading ? (
                <span className="text-sm text-gray-500">Calculating...</span>
              ) : (
                <>
                  {service.price > 0 && (
                    <button
                      onClick={() => setShowBreakdown((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-600 transition"
                      title="View price breakdown"
                    >
                      <FaInfoCircle size={16} />
                    </button>
                  )}

                  {originalSnapshot.current?.price != null &&
                  service.price !== originalSnapshot.current.price ? (
                    <>
                      <span className="text-gray-400 line-through text-sm">
                        ₹{originalSnapshot.current.price.toLocaleString()}
                      </span>
                      <span className="font-bold text-green-600">
                        ₹{service.price?.toLocaleString() ?? "0"}
                      </span>
                    </>
                  ) : (
                    <span className="font-bold text-green-600">
                      ₹{service.price?.toLocaleString() ?? "0"}
                    </span>
                  )}

                  {hasChanges && (
                    <button
                      onClick={handleReset}
                      className="bg-white border border-gray-300 rounded-full p-1.5 hover:bg-gray-50 transition"
                      title="Reset to original values"
                    >
                      <FaUndo size={14} className="text-gray-500" />
                    </button>
                  )}

                  {showBreakdown && service.price > 0 && (
                    <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4 text-sm">
                      <div className="font-semibold text-gray-800 mb-3 border-b pb-2">
                        Price Breakdown
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Price</span>
                          <span>
                            ₹
                            {(
                              (service.base_total ||
                              service.price -
                                (service.gst_amount || 0)) -
                                surchargeTotal
                            ).toLocaleString()}
                          </span>
                        </div>

                        {surchargeList.length > 0 && (
                          <div className="pl-3 border-l-2 border-gray-200 ml-1">
                            {surchargeList.map((s, i) => (
                              <div key={i} className="flex justify-between">
                                <span className="text-gray-500 text-xs capitalize">
                                  {s.name?.replace(/_/g, " ") || "Surcharge"}
                                </span>
                                <span className="text-xs text-green-600">
                                  +₹{s.price?.toLocaleString() || 0}
                                </span>
                              </div>
                            ))}
                            <div className="flex justify-between font-medium text-xs border-t border-gray-100 pt-1 mt-1">
                              <span>Total surcharges</span>
                              <span className="text-green-600">
                                +₹{surchargeTotal.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        )}

                        {service.discount_amount > 0 && (
                          <div className="flex justify-between text-red-500">
                            <span>Discount</span>
                            <span>
                              -₹{service.discount_amount?.toLocaleString() || 0}
                            </span>
                          </div>
                        )}

                        {service.gst_amount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Tax ({service.tax_percentage || 0}%)
                            </span>
                            <span>
                              ₹{service.gst_amount?.toLocaleString() || 0}
                            </span>
                          </div>
                        )}

                        <div className="flex justify-between font-bold text-green-700 border-t pt-1.5 mt-1.5">
                          <span>Total</span>
                          <span>₹{service.price?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {originalSnapshot.current?.price != null &&
                    service.price !== originalSnapshot.current.price && (
                      <div className="absolute top-full right-0 mt-1 text-xs font-medium">
                        {service.price > originalSnapshot.current.price ? (
                          <span className="text-red-500">
                            +₹
                            {(
                              service.price - originalSnapshot.current.price
                            ).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-green-600">
                            -₹
                            {(
                              originalSnapshot.current.price - service.price
                            ).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
            {/* <button
              onClick={onSave}
              className="inline-flex items-center gap-2 bg-gray-800 text-white px-3 py-1 rounded-xl font-semibold hover:bg-gray-900 transition disabled:opacity-50"
            >
              <FaCheckCircle /> Save
            </button> */}
          </div>
        </div>

        {/* ===== SECOND LINE: Service, time, staff ===== */}
        <div className="flex flex-wrap items-center gap-3 mb-2">
          {readOnly ? (
            <>
              <span className="font-medium text-gray-700">
                {selectedVetService?.name || service.serviceName || "—"}
              </span>
              <span className="text-gray-600">{selectedTime || "—"}</span>
              <span className="text-gray-600">
                {selectedStaff?.name || service.assignedStaff || "—"}
              </span>
            </>
          ) : (
            <>
              <CustomSelect
                value={serviceId}
                onChange={(e) => {
                  hasUserInteracted.current = true;
                  setServiceId(e.target.value);
                  // setStaffId("");
                }}
                disabled={false}
                loading={false}
                options={vetServices}
                placeholder="Select service"
                className="flex-1 max-w-lg"
                renderOption={(opt) => {
                  const mrpPrice = Number(
                    opt.mrp_price || opt.basic_price || 0,
                  );
                  const salePrice = Number(
                    opt.sale_price || opt.basic_price || 0,
                  );
                  const hasDiscount = salePrice < mrpPrice;
                  const isFree = opt.discount_percentage === 100;
                  const displayPrice = isFree ? 0 : salePrice;
                  if (hasDiscount)
                    return `${opt.name} (₹${mrpPrice} → ₹${displayPrice})`;
                  return `${opt.name} (₹${mrpPrice})`;
                }}
              />
              <CustomSelect
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                disabled={!selectedDate || !timeSlots.length}
                loading={false}
                options={timeSlots.map((slot) => ({ _id: slot, name: slot }))}
                placeholder={
                  !selectedDate ? "Select date first" : "Select time"
                }
                className="w-36"
                labelKey="name"
                valueKey="_id"
              />
              <CustomSelect
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                disabled={!serviceId || loadingStaff}
                loading={loadingStaff}
                options={availableStaff}
                placeholder="Select staff"
                className="w-40"
                labelKey="name"
                valueKey="_id"
              />
            </>
          )}
        </div>

        {/* Addons / products / other charges */}
        <BookingAddonsSection
          addons={service.addons || []}
          products={service.products || []}
          otherCharges={service.otherCharges || []}
          onAddonsChange={(newAddons) =>
            onUpdate({ ...service, addons: newAddons })
          }
          onProductsChange={(newProducts) =>
            onUpdate({ ...service, products: newProducts })
          }
          onOtherChargesChange={(newCharges) =>
            onUpdate({ ...service, otherCharges: newCharges })
          }
          serviceCategoryId={selectedServiceDetails?.category_type_id}
          petTypeId={pet.pet_type_id}
          serviceTypeName={"Vet"}
        />

        {/* ===== VITAL SIGNS ===== */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Vital Signs
          </h4>
          {readOnly ? (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {VITAL_FIELDS.map(({ key, label, unit }) => {
                const val = service.vitals?.[key] ?? vitals[key];
                if (!val && val !== 0) return null;
                return (
                  <div key={key} className="text-sm">
                    <span className="text-gray-500">{label}: </span>
                    <span className="text-gray-800 font-medium">
                      {val}
                      {unit}
                    </span>
                  </div>
                );
              })}
              {VITAL_FIELDS.every(
                ({ key }) => !service.vitals?.[key] && !vitals[key],
              ) && (
                <p className="text-sm text-gray-400 italic col-span-full">
                  No vitals recorded
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {VITAL_FIELDS.map(({ key, label, unit, placeholder }) => (
                <div key={key} className="relative">
                  <label className="text-xs text-gray-500 mb-0.5 block">
                    {label}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={vitals[key] ?? ""}
                    onChange={(e) => handleVitalChange(key, e.target.value)}
                    placeholder={placeholder}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-800 focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  />
                  {unit && (
                    <span className="absolute right-2 top-[26px] text-xs text-gray-500 pointer-events-none">
                      {unit}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Customer Notes (read‑only) */}
        {service.notes && (
          <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-700">
            <span className="font-semibold text-gray-500">Customer Notes:</span>
            <p className="mt-1 whitespace-pre-wrap">{service.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VetServiceCheckinCard;
